import {
  DialogContent,
  Grid,
  InputLabel,
  MenuItem,
  FormControl,
  Collapse,
  Stack,
  IconButton,
  Divider,
  Chip,
  Autocomplete,
  useMediaQuery,
  Typography,
  Tooltip,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDDropDown from "components/MDDropDown";
import { useMaterialUIController } from "context";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import CircularIndeterminate from "components/MDLoading";
import MDSnackbar from "components/MDSnackbar";
import { chatBotMessagePurpose } from "global/constants";
import MDBox from "components/MDBox";
import { TransitionGroup } from "react-transition-group";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import RichTextEditor from "components/UIComponents/RichTextEditor/RichTextEditor";
import MDInput from "components/MDInput";
import { useDispatch, useSelector } from "react-redux";
import { createMessage } from "store/slice/message/messageSlice";
import { SMSMessagePurpose } from "global/constants";
import moment from "moment";
import { DateRangePicker } from "rsuite";
import { getMessage } from "store/slice/message/messageSlice";
import { getCalendarDataForMessages } from "store/slice/message/messageSlice";
import { purposeTags } from "global/constants";

const TAG_DESCRIPTIONS = {
  brandName:
    "Indicates the company or brand name so users instantly recognize who the message is from.",
  placeAddress:
    "Shows the complete address of the location to help users identify where they need to go.",
  lotName:
    "Represents the name of the specific parking lot where the customer is parking.",
  displayName:
    "Displays the selected parking rate name, making it easy for users to identify their choice.",
  parentDisplayName:
    "Shows the name of the parent or main rate when the selected rate belongs to a group.",
  rateTitle:
    "Provides the title or name of the parking rate to help users understand the rate type.",
  amount: "Indicates the price of the selected parking rate.",
  baseRate: "Shows the original parking rate before applying fees or taxes.",
  serviceFee:
    "Represents any additional service or processing fee applied to the booking.",
  tax: "Displays the total tax added to the parking cost.",
  cityTax: "Shows the tax amount specific to the city.",
  countyTax: "Shows the tax amount charged at the county level.",
  totalAmount:
    "Indicates the final payable amount including all fees and taxes.",
  licensePlate:
    "Displays the vehicle’s license plate number provided by the customer.",
  validationLink:
    "Provides a link that customers can use to validate their parking.",
  startDate: "Shows the date when the parking session begins.",
  startTime: "Displays the exact time the parking session starts.",
  endDate: "Shows the date when the parking session ends.",
  endTime: "Displays the time when the parking session ends.",
  receiptURL:
    "Provides a link where customers can view or download their parking receipt.",
  extensionLink:
    "Shows a link that allows the customer to extend their parking session.",
  updateLicensePlateLink:
    "Shows a link that allows the customer to update their current license plate.",
  plivoNumber: "Number that user get SMS from this number",
};

const CreateChatBotMessage = (props) => {
  const { dialogOpen, onClose, flowType, selectedDay, ratesData } = props;
  const theme = useTheme();
  const textMessageRef = useRef(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const location = useLocation();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  const placesData = useSelector((state) => state.places?.selectedPlace);
  let placeId =
    placesData?._id || new URLSearchParams(location?.search).get("placeId");

  const purposeOptions = useMemo(() => {
    const baseList =
      flowType === "BOTCOPY" ? chatBotMessagePurpose : SMSMessagePurpose;
    if (
      flowType === "BOTCOPY" &&
      Array.isArray(ratesData) &&
      ratesData.length > 0
    ) {
      const dynamicPurposes = [];
      ratesData.forEach((rate) => {
        if (rate?.isRateOption) {
          const name = rate?.displayName?.trim();
          if (name) {
            const onRateSel = `${name} (On Rate Selection)`;
            const extWelcome = `${name} (Extension Welcome)`;
            if (!baseList.some((item) => item.title === onRateSel)) {
              dynamicPurposes.push({ title: onRateSel });
            }
            if (!baseList.some((item) => item.title === extWelcome)) {
              dynamicPurposes.push({ title: extWelcome });
            }
          }
        }
      });
      return [...baseList, ...dynamicPurposes];
    }
    return baseList;
  }, [flowType, ratesData]);

  const validationSchema = Yup.object().shape({
    purpose: Yup.string().required("Purpose is required"),
    days: Yup.array().required("Days are required").min(1, "Days are required"),
  });

  const handelCreateMessage = (data) => {
    setIsLoading(true);
    dispatch(createMessage(data))
      .unwrap()
      .then((res) => {
        console.log("Message creation response:", res);
        const success = res?.success;
        setIsLoading(false);
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
        if (success) {
          const payload = {
            selectedDay,
            placeId,
          };
          dispatch(getMessage(payload));
          dispatch(getCalendarDataForMessages(payload));
          onClose();
        }
      })
      .catch((err) => {
        console.error("Error creating Message:", err);
        setIsLoading(false);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message,
          icon: "warning",
          show: true,
        });
      });
  };

  const Days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "All",
  ];

  useEffect(() => {
    document.body.style.overflowY =
      dialogOpen || isMobile ? "hidden !important" : "";

    return () => {
      document.body.style.overflowY = "";
    };
  }, [dialogOpen, isMobile]);

  const insertTag = (fieldName, fieldValue, tag, inputRef, setFieldValue) => {
    const tagText = `{{${tag}}}`;

    const inputEl = inputRef?.current;

    if (!inputEl || typeof inputEl.selectionStart !== "number") {
      setFieldValue(fieldName, `${fieldValue || ""}${tagText}`);
      return;
    }

    const cursorPos = inputEl.selectionStart ?? fieldValue.length;

    const before = fieldValue.slice(0, cursorPos);
    const after = fieldValue.slice(cursorPos);

    setFieldValue(fieldName, `${before}${tagText}${after}`);

    setTimeout(() => {
      inputEl.focus();

      const newPos = cursorPos + tagText.length;

      inputEl.setSelectionRange(newPos, newPos);
    }, 0);
  };

  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Creating Message.. "
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDDialog
        dialogTitle={`Create ${flowType} Message`}
        open={dialogOpen}
        dialogClose={onClose}
        closeIcon={true}
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogContent>
          <Formik
            enableReinitialize={!isMobile}
            initialValues={{
              placeId: placeId,
              purpose: "Welcome Message",
              title: "",
              subTitle: "",
              buttonText: "",
              simpleMessages: [
                {
                  message: "",
                },
              ],
              flowType: flowType,
              textMessage: "",
              startTime: new Date(new Date().setHours(0, 0, 0)),
              endTime: new Date(new Date().setHours(23, 59, 59)),
              days: [],
            }}
            validationSchema={validationSchema}
            onSubmit={(value, actions) => {
              console.log("values", value);
              const payload = {
                ...value,

                title: value.title?.replaceAll(
                  /class="wysiwyg-mention"/g,
                  `style="pointer-events:none;text-decoration:none;color:unset;background-color:unset;"`,
                ),

                subTitle: value.subTitle?.replaceAll(
                  /class="wysiwyg-mention"/g,
                  `style="pointer-events:none;text-decoration:none;color:unset;background-color:unset;"`,
                ),

                startTime: moment(value.startTime).format("HH:mm"),
                endTime: moment(value.endTime).format("HH:mm"),

                simpleMessages: value.simpleMessages?.map((message) => ({
                  message: message?.message.replaceAll(
                    /class="wysiwyg-mention"/g,
                    `style="pointer-events:none;text-decoration:none;color:unset;background-color:unset;"`,
                  ),
                })),
              };
              if (
                value.purpose === "Welcome Message (SMS)" &&
                !value.textMessage.includes("{{chatbotURL}}")
              ) {
                payload.textMessage = `${value.textMessage} {{chatbotURL}}`;
              }

              handelCreateMessage(payload);
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <Grid container spacing={2} className="mt-1">
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        Select Purpose
                      </InputLabel>
                      <MDDropDown
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Select Purpose"
                        name="purpose"
                        value={props.values.purpose}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        error={props.errors.purpose && props.touched.purpose}
                        helperText={
                          props.errors.purpose &&
                          props.touched.purpose &&
                          props.errors.purpose
                        }
                      >
                        {purposeOptions.map((item) => (
                          <MenuItem key={item.title} value={item.title}>
                            {item.title}
                          </MenuItem>
                        ))}
                      </MDDropDown>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <DateRangePicker
                        id="timeDuration"
                        placeholder="Start Time - End Time"
                        character=" - "
                        size="lg"
                        format="hh:mm a"
                        showMeridian
                        value={
                          props.values.startTime && props.values.endTime
                            ? [
                                new Date(props.values.startTime),
                                new Date(props.values.endTime),
                              ]
                            : [
                                new Date(new Date().setHours(0, 0, 0)), // Set default start time to 12:00 AM
                                new Date(new Date().setHours(23, 59, 59)), // Set default end time to 11:59 PM
                              ]
                        }
                        ranges={[]}
                        onChange={(value) => {
                          console.log("value ====>", value);
                          if (value && value.length === 2) {
                            props.setFieldValue("startTime", value[0]);
                            props.setFieldValue("endTime", value[1]);
                          } else {
                            props.setFieldValue("startTime", null);
                            props.setFieldValue("endTime", null);
                          }
                        }}
                        onBlur={props.handleBlur}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      disableClearable
                      name="days"
                      value={props.values?.days || []}
                      options={Days.filter(
                        (option) => !props.values?.days?.includes(option),
                      )}
                      onChange={(event, newValues) => {
                        props.setValues({
                          ...props?.values,
                          days:
                            newValues.indexOf("All") === -1
                              ? newValues
                              : [
                                  "Sunday",
                                  "Monday",
                                  "Tuesday",
                                  "Wednesday",
                                  "Thursday",
                                  "Friday",
                                  "Saturday",
                                ], // Update rateType to an array of selected IDs
                        });
                      }}
                      onBlur={props.handleBlur}
                      getOptionLabel={(option) => option} // Display the label (rate name)
                      renderInput={(params) => (
                        <MDInput
                          label="Days"
                          fullWidth={true}
                          helperText={
                            props.errors.days &&
                            props.touched.days &&
                            props.errors.days
                          }
                          {...params}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={index}
                            variant="contained"
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  </Grid>

                  {props.values.purpose === "License Plate Form" ||
                  props.values.purpose === "Pass Form" ||
                  props.values.purpose === "Monthly Rate Form" ||
                  props.values.purpose ===
                    "Space Selection Form (Unit Parking)" ||
                  props.values.purpose ===
                    "Additional License Plate Form (Unit Parking)" ||
                  props.values.purpose ===
                    "Rate Selection Form (Unit Parking)" ||
                  props.values.purpose === "Payment Card (Monthly)" ||
                  props.values.purpose === "Payment Card" ||
                  props.values.purpose === "Permit Creation Form" ? (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="button"
                          fontWeight="medium"
                          sx={{ mb: 1, display: "block" }}
                        >
                          Title
                        </Typography>

                        <RichTextEditor
                          purpose={props.values.purpose}
                          name="title"
                          value={props.values.title}
                          setFieldValue={props.setFieldValue}
                          error={props.errors.title}
                          helperText={props.errors.title}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="button"
                          fontWeight="medium"
                          sx={{ mb: 1, display: "block" }}
                        >
                          Sub Title
                        </Typography>

                        <RichTextEditor
                          purpose={props.values.purpose}
                          name="subTitle"
                          value={props.values.subTitle}
                          setFieldValue={props.setFieldValue}
                          error={props.errors.subTitle}
                          helperText={props.errors.subTitle}
                        />
                      </Grid>
                    </>
                  ) : null}

                  {[
                    "Extension Link Expire",
                    "Validation Link Expire",
                    "Welcome Message (Validation)",
                    "Welcome Message",
                    "On Rate Selection",
                    "Payment Confirmation",
                    "Payment Confirmation (Monthly)",
                    "On Hour Selection",
                    "Unavailability",
                    "Space Unavailability",
                    "Unavailability (Extension)",
                    "Help",
                    "Learn More",
                    "Contact",
                    "Extension Welcome",
                    "License Plate Update",
                    "License Plate Update Success",
                    "Welcome Message (Parent Rate)",
                    "Second Step Validation Message",
                    "After LicensePlate Submission (Two Step Validation)",
                    "Payment Confirmation (Two Step Validation)",
                    "Welcome Message (Pay Now Validation Later Flow)",
                    "Payment Confirmation (Pay Now Validation Later Flow)",
                    "Permit Form Submit",
                    "Welcome Message (Permit)",
                    "Welcome Message (Unit Parking)",
                    "Welcome Message (VIP Parking)",
                    "Detail Confirmation Message (Unit Parking)",
                    "License Plate Confirmation",
                    "Free Parking Limit Reached",
                  ].includes(props.values.purpose) ||
                  props.values.purpose?.endsWith("(On Rate Selection)") ||
                  props.values.purpose?.endsWith("(Extension Welcome)") ? (
                    <Grid item xs={12}>
                      <FieldArray name="simpleMessages">
                        {({ remove, push }) => (
                          <MDBox className="pt-2">
                            <TransitionGroup component="div" appear>
                              {props.values.simpleMessages?.map(
                                (message, index) => (
                                  <Collapse key={index}>
                                    <MDBox>
                                      {index !== 0 && (
                                        <MDBox mt={1}>
                                          <Stack
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            spacing={1}
                                          >
                                            <Divider
                                              orientation="horizontal"
                                              flexItem
                                              light
                                              sx={{
                                                width: `calc(100% - 40px)`,
                                                margin: "auto 0 !important",
                                              }}
                                            />
                                            {props.values.simpleMessages
                                              .length > 1 && (
                                              <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => remove(index)}
                                              >
                                                <ClearOutlinedIcon />
                                              </IconButton>
                                            )}
                                          </Stack>
                                        </MDBox>
                                      )}
                                      <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                          <MDBox>
                                            <RichTextEditor
                                              purpose={props.values.purpose}
                                              name={`simpleMessages.${index}.message`}
                                              value={message.message}
                                              setFieldValue={
                                                props.setFieldValue
                                              }
                                              error={
                                                props.errors.simpleMessages?.[
                                                  index
                                                ]?.message
                                              }
                                              helperText={
                                                props.errors.simpleMessages?.[
                                                  index
                                                ]?.message
                                              }
                                            />
                                          </MDBox>
                                        </Grid>
                                      </Grid>
                                    </MDBox>
                                  </Collapse>
                                ),
                              )}
                            </TransitionGroup>
                            <MDBox sx={{ mr: 2, mt: 3, mb: 1 }}>
                              <MDButton
                                variant="contained"
                                color={sidenavColor}
                                onClick={() => push({ message: "" })}
                              >
                                +
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        )}
                      </FieldArray>
                    </Grid>
                  ) : null}

                  {(props.values.purpose === "Payment Card" ||
                    props.values.purpose === "Payment Card (Monthly)") && (
                    <>
                      <Grid item xs={12}>
                        <MDInput
                          name="buttonText"
                          value={props.values.buttonText}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          label="Button Text"
                          error={
                            props.errors.buttonText && props.touched.buttonText
                              ? true
                              : false
                          }
                          success={
                            props.errors.buttonText && props.touched.buttonText
                              ? false
                              : true
                          }
                          helperText={
                            props.errors.buttonText && props.touched.buttonText
                              ? props.errors.buttonText
                              : null
                          }
                        />
                      </Grid>
                    </>
                  )}
                  {[
                    "Extension Reminder",
                    "Payment Confirmation SMS",
                    "Help SMS",
                    "Validation Reminder SMS",
                    "After LicensePlate Submission SMS (Two Step Validation)",
                    "Welcome Message (SMS)",
                  ].includes(props.values.purpose) && (
                    <>
                      <Grid item xs={12}>
                        <MDInput
                          name="textMessage"
                          value={props.values.textMessage}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          label="Message"
                          inputProps={{ ref: textMessageRef }}
                          error={Boolean(
                            props.errors.textMessage &&
                            props.touched.textMessage,
                          )}
                          success={
                            !props.errors.textMessage &&
                            props.touched.textMessage
                          }
                          helperText={
                            props.errors.textMessage &&
                            props.touched.textMessage
                              ? props.errors.textMessage
                              : null
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        {(() => {
                          const activeTags =
                            purposeTags[props.values.purpose] ||
                            (props.values.purpose?.endsWith("(On Rate Selection)")
                              ? purposeTags["On Rate Selection"]
                              : null) ||
                            (props.values.purpose?.endsWith("(Extension Welcome)")
                              ? purposeTags["Extension Welcome"]
                              : null) ||
                            [];

                          return activeTags.length > 0 ? (
                            <Box
                              mt={1}
                              sx={{
                                borderTop: "1px solid #eee",
                                paddingTop: "8px",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "6px",
                              }}
                            >
                              {activeTags.map((tag) => (
                                <Tooltip
                                  key={tag}
                                  title={
                                    TAG_DESCRIPTIONS[tag] ||
                                    "No description available"
                                  }
                                  arrow
                                >
                                  <Chip
                                    label={`{{${tag}}}`}
                                    size="small"
                                    clickable
                                    color={sidenavColor}
                                    variant="outlined"
                                    sx={{ cursor: "pointer", fontSize: "12px" }}
                                    onClick={() =>
                                      insertTag(
                                        "textMessage",
                                        props.values.textMessage,
                                        tag,
                                        textMessageRef,
                                        props.setFieldValue,
                                      )
                                    }
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption">
                              This purpose does not have any dynamic parameters.
                            </Typography>
                          );
                        })()}
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} className="text-right">
                    <MDButton
                      variant="contained"
                      color={sidenavColor}
                      type="submit"
                    >
                      Create
                    </MDButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </MDDialog>
    </>
  );
};

export default CreateChatBotMessage;
